import { ApiError, del, get } from "./api";

/**
 * A member of the caller's organization, as the admin users table needs them.
 *
 * Richer than `getOrganizationMembers` in `services/calendar`, which exists for the calendar's
 * assignee picker and deliberately returns no sensitive fields — no email, no activity.
 */
export type OrganizationUser = {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  createdAt: string;
  lastLoginAt: string | null;
};

/** Every active user in the caller's organization. Admin only. */
export const getUsers = async (): Promise<OrganizationUser[]> => {
  return get<OrganizationUser[]>("/api/users");
};

/** Display name, falling back to the email when a user has no name on file yet. */
export const userDisplayName = (user: OrganizationUser): string => {
  const full = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return full || user.email;
};

/**
 * Two letters for the avatar, mirroring how the API builds them for the calendar picker so the same
 * person is not abbreviated two different ways in two places.
 */
export const userInitials = (user: OrganizationUser): string => {
  const first = (user.firstName ?? "").trim();
  const last = (user.lastName ?? "").trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first) return first[0].toUpperCase();
  return (user.email[0] ?? "?").toUpperCase();
};

/* ── Removing someone ── */

/** Why a removal was refused, in the terms the user's page renders. */
export type UserRemovalFailureKind =
  /** 409 or 400. The server refused: your own account, the last administrator, a platform account. */
  | "refused"
  /** 404. Unknown, or already removed. A repeat delete lands here. */
  | "not_found"
  /** Anything else, including a request that never arrived. */
  | "failed";

/** A refused removal, carrying the message to show. */
export class UserRemovalError extends Error {
  readonly kind: UserRemovalFailureKind;

  constructor(kind: UserRemovalFailureKind, message: string) {
    super(message);
    this.name = "UserRemovalError";
    this.kind = kind;
  }
}

/**
 * Copy for when the server sent none of its own.
 *
 * The refused case has to cover both guards in one sentence, because a 409 alone does not say
 * which one fired. In practice the server does send its message and it names the reason exactly,
 * so this is the fallback, not the usual answer.
 */
const REMOVAL_MESSAGES: Record<UserRemovalFailureKind, string> = {
  refused:
    "This person cannot be removed. You cannot remove your own account, and an organization has to keep at least one administrator.",
  not_found: "That person has already been removed.",
  failed: "That person could not be removed. Please try again.",
};

/**
 * Removes someone from the caller's organization.
 *
 * **The server owns the guards**, and refuses two cases with a 409: deleting your own account, and
 * deleting the organization's last administrator. Both come back with a message saying which, and
 * that message is preferred over anything written here, because it is the one that distinguishes
 * them. The screen hides this control on your own account anyway; the self guard is what catches a
 * stale tab.
 */
export const removeUser = async (userId: string): Promise<void> => {
  try {
    await del<unknown>(`/api/users/${encodeURIComponent(userId)}`);
  } catch (error) {
    if (!(error instanceof ApiError)) {
      throw new UserRemovalError("failed", REMOVAL_MESSAGES.failed);
    }

    const kind: UserRemovalFailureKind =
      error.statusCode === 409 || error.statusCode === 400
        ? "refused"
        : error.statusCode === 404
          ? "not_found"
          : "failed";

    // A server message is only used when it is a message: the api client falls back to
    // "API request failed: 409 Conflict", which is not something to show a coach.
    const fromServer =
      error.message && !error.message.startsWith("API request failed")
        ? error.message
        : null;

    throw new UserRemovalError(kind, fromServer ?? REMOVAL_MESSAGES[kind]);
  }
};
